import creditImg from '../assets/blog/cards/credit.jpg';
import cuendoImg from '../assets/blog/cards/cuendo.jpg';
import saisImg from '../assets/blog/cards/sais.jpg';

import type { Work } from './types/work';

export const works: Work[] = [
  {
    id: 'sais',
    slug: 'sais',
    title: 'Scaling Digital Innovation Across Africa: Partnership with SAIS & GIZ',
    tags: ['Startups', 'Agriculture', 'Innovation'],
    description:
      'Hands-on digital transformation support for 12 agriculture-focused startups across Sub-Saharan Africa through GIZ\'s SAIS program.',
    longDescription:
      "Agriculture is central to Africa's economy and livelihoods — but many agri-focused startups struggle to scale. Through GIZ's SAIS program, Ounic delivered hands-on digital transformation support to 12 startups across Sub-Saharan Africa.",
    image: saisImg,
    authorId: 'tonny-shimanyi',
    date: '2025-02-16',
    client: 'Deutsche Gesellschaft für Internationale Zusammenarbeit (GIZ) GmbH',
    caseStudy: {
      about:
        "Agriculture is central to Africa's economy and livelihoods — but many agri-focused startups struggle to scale. Through GIZ's SAIS program, Ounic delivered hands-on digital transformation support to 12 startups across Sub-Saharan Africa.",
      challenge:
        'The SAIS program brought together 12 agriculture-focused startups at different stages of digital maturity. Each needed tailored tools, systems, and expertise to scale efficiently and attract investment — without a one-size-fits-all playbook.',
      solution:
        'Ounic worked through three structured phases with each startup: data-driven needs assessment, hands-on implementation and co-building with agile delivery, and capacity building through workshops and shared documentation — ensuring teams could independently manage and iterate on the systems introduced.',
      results:
        'While each startup\'s needs were unique, four categories of improvement emerged consistently across the cohort — from stronger digital infrastructure to scalable foundations ready for growth. This collaboration with SAIS and GIZ reflects what Ounic believes: technology, strategy, and genuine partnership can transform how Africa\'s agricultural sector operates.',
      stats: [
        { value: '12', label: 'Startups', context: 'Supported across Africa' },
        { value: '5+', label: 'Service areas', context: 'Delivered' },
        { value: 'SDG 2', label: 'Zero Hunger', context: 'Core program goal' },
      ],
      infographics: {
        statStrip: [
          { value: '12', label: 'Startups supported across Africa' },
          { value: '5+', label: 'Service areas delivered' },
          { value: 'SDG 2', label: 'Zero Hunger — a core program goal' },
        ],
        fields: {
          eyebrow: 'The mission',
          title: 'Investment-ready startups, at scale',
          description:
            'The shared objective was to equip each startup with the tools, systems, and expertise needed to scale efficiently and attract investment. Ounic provided end-to-end support across five disciplines.',
          items: [
            { label: 'Software engineering', icon: 'bx-code-alt' },
            { label: 'Web & mobile development', icon: 'bx-mobile' },
            { label: 'System architecture', icon: 'bx-server' },
            { label: 'Digital marketing', icon: 'bx-broadcast' },
            { label: 'UX & product design', icon: 'bx-layout' },
          ],
        },
        approach: {
          eyebrow: 'Our approach',
          title: 'Practical, tailored, and impact-focused',
          description:
            'Rather than applying a one-size-fits-all playbook, Ounic worked through three structured phases with each startup.',
          items: [
            {
              step: 1,
              title: 'Data-driven needs assessment',
              description:
                "Structured surveys, interviews, and technical assessments identified each startup's gaps and priorities — producing tailored intervention plans aligned with real operational realities, not assumptions.",
            },
            {
              step: 2,
              title: 'Hands-on implementation & co-building',
              description:
                'Ounic went beyond advisory. Using agile methodologies, the team worked directly with startup founders and developers to build web and mobile apps, design APIs, create UI/UX prototypes, and deploy automation pipelines.',
            },
            {
              step: 3,
              title: 'Capacity building at scale',
              description:
                'Workshops, live co-development sessions, and shared documentation ensured each team could independently manage and iterate on the systems introduced — building lasting capability, not dependency.',
            },
          ],
        },
        impacts: {
          eyebrow: 'What we delivered',
          title: 'Ecosystem-wide impact',
          description:
            "While each startup's needs were unique, four categories of improvement emerged consistently across the cohort.",
          items: [
            {
              title: 'Stronger digital infrastructure',
              description:
                'Modernised web, mobile, and backend systems with improved architecture and scalability.',
              icon: 'bx-bolt',
            },
            {
              title: 'Data-driven decisions',
              description:
                'Database design, KPI frameworks, and data pipelines enabling startups to understand and optimise their operations.',
              icon: 'bx-bar-chart-alt-2',
            },
            {
              title: 'Market positioning',
              description:
                'Stronger brand identities, structured marketing plans, and improved customer engagement.',
              icon: 'bx-broadcast',
            },
            {
              title: 'Scalable foundations',
              description:
                'API-driven systems, automated deployments, and cloud infrastructure ready to handle growth.',
              icon: 'bx-cloud',
            },
          ],
        },
        outcomes: {
          eyebrow: 'Measuring success',
          title: 'From outputs to lasting impact',
          description:
            'The program tracked results across three levels — what was delivered, what changed, and what it meant for the communities these startups serve.',
          items: [
            {
              label: 'Outputs',
              items: [
                'Increased platform usage',
                'Improved system reliability',
                'Products ready for market expansion',
              ],
            },
            {
              label: 'Outcomes',
              items: [
                'Stronger unit economics',
                'Better operational decisions',
                'Increased ability to attract funding',
              ],
            },
            {
              label: 'Impact',
              items: [
                'Improved income for end-users',
                'Stronger digital ag ecosystems',
                'Contribution to SDG 2 (Zero Hunger)',
              ],
            },
          ],
        },
        learnings: {
          eyebrow: 'Key learnings',
          title: 'What the program taught us',
          items: [
            {
              text: 'Tailored solutions consistently outperform generic interventions — context matters more than frameworks.',
            },
            {
              text: 'Early investment in data analytics creates compounding advantages as startups grow.',
            },
            {
              text: 'Technology adoption without knowledge transfer creates fragile systems — capacity building is non-negotiable.',
            },
            {
              text: 'Agile, collaborative delivery unlocks faster results and keeps teams aligned as priorities shift.',
            },
          ],
        },
      },
    },
  },
  {
    id: 'crf',
    slug: 'crf',
    title: 'Rebuilding Financial Infrastructure: The Credit Rotation Fund Platform',
    tags: ['Finance', 'Financial Infrastructure'],
    description:
      'What started as a technical audit of an existing loan management system evolved into a full platform rebuild — delivering a production-ready CRF system designed for scale.',
    longDescription:
      "Digital transformation often begins with understanding what's broken. What started as a technical audit of an existing loan management system evolved into a full platform rebuild — delivering a production-ready CRF system designed for scale.",
    image: creditImg,
    authorId: 'tonny-shimanyi',
    date: '2025-02-16',
    caseStudy: {
      about:
        "Digital transformation often begins with understanding what's broken. What started as a technical audit of an existing loan management system evolved into a full platform rebuild — delivering a production-ready CRF system designed for scale.",
      challenge:
        'The Credit Rotation Fund (CRF) platform was designed to manage the full loan lifecycle — origination, servicing, repayments, reporting, and beneficiary tracking. The foundations were there, but an audit revealed significant issues holding the platform back from real-world use.',
      solution:
        'Before writing new code, Ounic conducted a comprehensive technical and product audit covering the codebase, database design, core modules, and UI/UX flows. With five critical findings in hand, the team moved into full delivery — rebuilding the database architecture, redesigning the user experience, streamlining loan workflows, and improving engineering quality with reusable components and structured deployment pipelines.',
      results:
        'The rebuilt CRF platform is a fully functional, scalable system capable of supporting real operational needs from day one. Financial systems like CRF are critical infrastructure — they determine who gets access to capital and how efficiently it is managed. At Ounic, we believe great platforms are built on clarity, usability, and strong technical foundations.',
      stats: [
        { value: '5', label: 'Critical findings', context: 'From technical audit' },
        { value: '4', label: 'Delivery pillars', context: 'Production rebuild' },
        { value: '100%', label: 'Loan lifecycle', context: 'End-to-end' },
      ],
      infographics: {
        fields: {
          eyebrow: 'The problem',
          title: 'A system with potential but critical gaps',
          description:
            'The foundations were there, but an audit revealed significant issues holding the platform back from real-world use.',
          items: [
            { label: 'Fragmented user experience across modules', icon: 'bx-grid-alt' },
            { label: 'Weak database structure limiting performance and scale', icon: 'bx-data' },
            { label: 'Limited role management and access control', icon: 'bx-lock-open' },
            { label: 'No structured deployment pipelines or dev workflows', icon: 'bx-git-branch' },
            { label: 'Inconsistent logic in core loan processing workflows', icon: 'bx-error' },
          ],
        },
        approach: {
          eyebrow: 'Phase one',
          title: 'Audit first — then build for scale',
          description:
            'Before writing a single line of new code, Ounic conducted a comprehensive technical and product audit. Five critical findings shaped everything that followed.',
          items: [
            {
              title: 'Disconnected module flows',
              description:
                'No cohesive navigation or handoff between modules, creating friction and confusion for everyday users.',
              icon: 'bx-shuffle',
            },
            {
              title: 'Database architecture issues',
              description:
                'Duplicate data, unclear entity relationships, and performance bottlenecks that would worsen under load.',
              icon: 'bx-data',
            },
            {
              title: 'Logic and calculation inconsistencies',
              description:
                'Core functions including loan calculations contained errors that undermined reliability across the platform.',
              icon: 'bx-calculator',
            },
            {
              title: 'Raw error exposure',
              description:
                'System errors surfaced directly to users with no meaningful messaging, and no structured logging for the development team.',
              icon: 'bx-error-circle',
            },
            {
              title: 'No reusable components or deployment structure',
              description:
                'The codebase was difficult to maintain and extend, with no consistent patterns or automated deployment workflows.',
              icon: 'bx-extension',
            },
          ],
        },
        impacts: {
          eyebrow: 'Phase two',
          title: 'From audit to implementation',
          description:
            'With a clear picture of what needed to change, Ounic moved into full delivery — addressing each finding with targeted, production-grade solutions.',
          items: [
            {
              title: 'Rebuilt database architecture',
              description:
                'Redesigned for data integrity, clear entity relationships, and performance under scale.',
              icon: 'bx-data',
            },
            {
              title: 'Redesigned user experience',
              description:
                'Intuitive workflows, improved dashboards, responsive design, and in-app notifications.',
              icon: 'bx-layout',
            },
            {
              title: 'Streamlined loan workflows',
              description:
                'End-to-end origination, servicing, and repayment flows reorganised for clarity and reliability.',
              icon: 'bx-receipt',
            },
            {
              title: 'Improved engineering quality',
              description:
                'Reusable components, structured deployment pipelines, and proper error handling with developer logging.',
              icon: 'bx-code-alt',
            },
          ],
        },
        learnings: {
          eyebrow: 'The result',
          title: 'A production-ready loan management platform',
          description:
            'The rebuilt CRF platform is a fully functional, scalable system capable of supporting real operational needs from day one.',
          items: [
            { text: 'Centralised financial and beneficiary data across the organisation' },
            { text: 'End-to-end loan lifecycle management — origination through to closure' },
            { text: 'Actionable reporting and portfolio analytics for better decision-making' },
            { text: 'A maintainable codebase that can evolve alongside the business' },
          ],
        },
        callout: {
          title: 'See it in action',
          description:
            'The platform is fully deployed and available for demonstration. Reach out to explore how it works and how it can be adapted to your needs.',
          icon: 'bx-slideshow',
        },
      },
    },
  },
  {
    id: 'cuendo',
    slug: 'cuendo',
    title: 'Automated Invoice Processing for Cuendo',
    tags: ['ML', 'Document Intelligence'],
    description:
      'A machine learning system that extracts structured invoice data at scale, replacing manual entry with an API-driven pipeline.',
    longDescription:
      "Invoice processing is one of those workflows that looks simple until you're doing it at scale. Ounic built a machine learning system for Cuendo — replacing manual data entry with an intelligent, API-driven pipeline.",
    image: cuendoImg,
    authorId: 'tonny-shimanyi',
    date: '2025-06-15',
    client: 'Cuendo',
    caseStudy: {
      about:
        'Cuendo needed a system that could read invoice images and return clean, machine-readable data for downstream systems. The architecture was designed with future extensibility in mind — service type categorisation such as engine, transmission, and bodywork is a natural next step the platform already supports.',
      challenge:
        'Cuendo needed to extract structured data from invoice documents reliably and at scale. The traditional approach — manual data entry — was slow, error-prone, and could not keep up with growing demand.',
      solution:
        'We built a Python 3 machine learning pipeline exposed through a FastAPI microservice, deployed with Docker on DigitalOcean. Invoice images are uploaded via API endpoints and returned as structured JSON responses in real time, with automatic alerts when CPU or memory exceeds 70%.',
      results:
        'The API-first pipeline significantly reduced manual data entry, freeing team time for higher-value work. Machine extraction removed the human error that comes with repetitive manual entry. The design means volume growth does not require additional headcount, and the modular architecture enables future additions like classification and analytics.',
      stats: [
        { value: '6', label: 'Core Fields', context: 'Per invoice' },
        { value: 'Real-time', label: 'API Responses', context: 'Structured JSON' },
        { value: '70%', label: 'Alert Threshold', context: 'CPU & memory' },
      ],
      infographics: {
        fields: {
          eyebrow: 'What the system extracts',
          title: 'The fields that matter',
          description:
            'The model was trained to accurately identify and extract six core fields from every invoice.',
          items: [
            { label: 'Invoice number', icon: 'bx-hash' },
            { label: 'Invoice date', icon: 'bx-calendar' },
            { label: 'Vehicle mileage', icon: 'bx-car' },
            { label: 'Service type', icon: 'bx-wrench' },
            { label: 'Repair shop details', icon: 'bx-building' },
            { label: 'Total cost', icon: 'bx-receipt' },
          ],
        },
        stack: {
          eyebrow: 'Under the hood',
          title: 'Technical architecture',
          description:
            'The solution was built on a modern, containerised stack designed for reliability and easy integration.',
          items: [
            { label: 'Core model', value: 'Python 3 ML pipeline' },
            { label: 'API layer', value: 'FastAPI microservice' },
            { label: 'Deployment', value: 'Docker + DigitalOcean' },
            { label: 'Monitoring', value: 'CPU & memory alerts' },
          ],
        },
        approach: {
          eyebrow: 'How we built it',
          title: 'Development process',
          description:
            'The project followed a structured ML lifecycle, from early data exploration through to production deployment.',
          items: [
            {
              step: 1,
              title: 'Problem definition & data exploration',
              description:
                'Understanding the invoice types and preparing a representative dataset.',
            },
            {
              step: 2,
              title: 'Algorithm selection',
              description:
                'Evaluating OCR technologies including Google Document AI and AWS Textract to find the right fit.',
            },
            {
              step: 3,
              title: 'Feature engineering',
              description:
                'Annotating invoice data to improve extraction accuracy across varied document formats.',
            },
            {
              step: 4,
              title: 'Model development',
              description:
                'Building, testing, and refining across training and validation datasets.',
            },
            {
              step: 5,
              title: 'Evaluation & reporting',
              description:
                'Measuring accuracy and iterating based on performance data.',
            },
            {
              step: 6,
              title: 'Deployment & integration',
              description:
                "Shipping as a live API integrated into Cuendo's existing systems.",
            },
          ],
        },
      },
    },
  },
];

export const worksById = Object.fromEntries(works.map((work) => [work.id, work])) as Record<string, Work>;
export const worksBySlug = Object.fromEntries(works.map((work) => [work.slug, work])) as Record<string, Work>;
